import * as React from 'react';
import { FC, useState } from 'react';
import { useLocales } from './locales';
import { Day, Choice as ChoiceType } from './types';
import { isRewarded, isDeposited, dayOfMonth } from './days';
import casino from './images/casino.png';
import poker from './images/poker.png';
import bets from './images/bets.png';
import received from './images/check_received.png';
import Gift from './images/Gift';

type Type = 'casino' | 'poker' | 'bets';

const images: Record<Type, string> = {
  casino,
  poker,
  bets,
};

const choiceType = (text: string): Type => {
  if (/freespin/i.test(text)) return 'casino';
  return /poker/i.test(text) ? 'poker' : 'bets';
};

const Rewarded: FC<{ day: Day }> = ({ day }) => {
  const l = useLocales();
  return (
    <div className="bonuses">
      <h3 className="bonuses_title b-title">
        {l('your_bonus')}{' '}
        <span className="b-title_red">
          {dayOfMonth(day.date)} {l('december')}
        </span>
      </h3>
      <div className="bonuses_info b-info" aria-labelledby="bonus-label">
        <div className="b-info_left">
          <div className="b-info_site bi-site">
            <div className="bi-site_image">
              <Gift />
            </div>
            <div className="bi-site_text">{l(choiceType(day.choice?.text || ''))}</div>
          </div>
          <div id="bonus-label" className="b-info_summary">
            {day.choice?.text}
          </div>
        </div>
        <div className="b-info_status bi-status">
          <div className="bi-status_image">
            <img src={received} alt="received" />
          </div>
          <div className="bi-status_text">{l('rewarded')}</div>
        </div>
      </div>
    </div>
  );
};

const Choice: FC<{
  choice: ChoiceType;
  selectedChoice: ChoiceType | undefined;
  setSelectedChoice: (c: ChoiceType) => void;
}> = ({ choice, selectedChoice, setSelectedChoice }) => {
  const l = useLocales();
  const type = choiceType(choice.text);
  const id = `${type}-bonus`;
  const selected = selectedChoice?.id === choice.id;
  return (
    <div
      key={type}
      aria-labelledby={id}
      className={`choice bonuses_item b-item ${selected ? 'selected' : ''}`}
      onClick={selected ? undefined : () => setSelectedChoice(choice)}
    >
      <div className="b-item_type">
        <Gift />
        <span>{l(type)}</span>
      </div>
      <div className={`b-item_info b-item_info__${type}`}>
        <div id={id} className="b-item_text">
          {choice.text}
        </div>
        <div className="b-item_image">
          <img src={images[type]} alt={type} />
        </div>
      </div>
    </div>
  );
};

type OnChoose = (choice: ChoiceType | undefined) => Promise<void>;

const SubmitBtn: FC<{ choice: ChoiceType | undefined; onChoose: OnChoose }> = ({ choice, onChoose }) => {
  const l = useLocales();
  const [buttonText, setButtonText] = useState(l('choose_bonus'));
  const onClick = () =>
    onChoose(choice).catch(() => {
      setButtonText(l('error'));
      setTimeout(() => setButtonText(l('choose_bonus')), 5000);
    });

  return (
    <button type="submit" className={choice ? '' : 'disabled'} onClick={onClick}>
      {buttonText}
    </button>
  );
};

type DepositedProps = { day: Day; onChoose: OnChoose };
const Deposited: FC<DepositedProps> = ({ day, onChoose }) => {
  const [selectedChoice, setSelectedChoice] = useState<ChoiceType>();
  const l = useLocales();

  return (
    <div className="bonuses">
      <h3 className="bonuses_title b-title">
        {l('your_bonuses')}{' '}
        <span className="b-title_red">
          {dayOfMonth(day.date)} {l('december')}
        </span>
      </h3>
      <div className="bonuses_list">
        {(day.offer?.choices || []).map((choice: ChoiceType) => (
          <Choice
            key={choice.id}
            choice={choice}
            selectedChoice={selectedChoice}
            setSelectedChoice={setSelectedChoice}
          />
        ))}
      </div>
      <div className="bonuses_choose">
        <SubmitBtn choice={selectedChoice} onChoose={onChoose} />
      </div>
    </div>
  );
};

const NotDeposited: FC<{ day: Day }> = ({ day }) => {
  const l = useLocales();

  return (
    <div className="bonuses">
      <h3 className="bonuses_title b-title">
        {l('your_bonuses')}{' '}
        <span className="b-title_red">
          {dayOfMonth(day.date)} {l('december')}
        </span>
      </h3>
      <div className="bonuses_no-deposit">{l('no_deposit')}</div>
    </div>
  );
};

const Bonuses: FC<{ day: Day | undefined; onBonusChoose: OnChoose }> = ({ day, onBonusChoose }) => {
  if (!day) return null;
  if (isRewarded(day)) return <Rewarded day={day} />;
  if (isDeposited(day)) return <Deposited day={day} onChoose={onBonusChoose} />;
  return <NotDeposited day={day} />;
};

export default Bonuses;
